/**
 * As novidades do produto que o modal apresenta.
 *
 * Cada novidade tem uma `chave` própria: é por ela que o servidor guarda o "já vi"
 * (`notification_reads`, tipo "novidade"), então **reaproveitar chave esconde a
 * novidade nova de quem viu a anterior**. Para desligar tudo, `NOVIDADES = []`.
 *
 * Duas formas de mostrar, e a diferença não é só visual:
 * - `video`: o filme do lançamento, com controles, som e legenda — a pessoa senta e
 *   assiste (Brik AI, 08/10/2026). O modal fica largo, no 16:9.
 * - `demo` + `passos`: a gravação curta do gesto, sem som, rodando em laço ao lado da
 *   lista do que acontece — a pessoa entende em 10 segundos e volta ao trabalho.
 *
 * `rotas` diz em que tela o modal abre sozinho. Sem o campo, abre em qualquer uma
 * (é o caso do lançamento); com `['/pipeline']`, só em Oportunidades.
 *
 * Sem o arquivo de mídia no ar, a novidade não aparece — nem o modal, nem a linha da
 * Home (ver `midiaNoAr`): aviso que anuncia um vídeo quebrado é pior que aviso nenhum.
 */

export interface PassoNovidade {
    titulo: string;
    descricao: string;
}

export interface Novidade {
    /** Vai para `notification_reads` (tipo "novidade") quando a pessoa clica em "Entendi". */
    chave: string;
    titulo: string;
    /** Uma linha sob o título do modal. */
    resumo: string;
    /** Em que telas o modal abre sozinho. Sem o campo, em qualquer uma. */
    rotas?: string[];
    /** O filme do lançamento: 16:9, com controles e som. */
    video?: string;
    /** A gravação do gesto: sem som, em laço, começa sozinha. */
    demo?: string;
    /** A mesma gravação feita com o app no modo escuro. Sem ela, vale a clara. */
    demoEscura?: string;
    /** Imagem antes do play. Sem ela, o navegador mostra o 1º quadro. */
    poster?: string;
    posterEscuro?: string;
    /** Legenda em WebVTT. Liga sozinha: muita gente assiste sem som. */
    legenda?: string;
    /** O que a demo mostra, em palavras — ao lado dela. */
    passos?: PassoNovidade[];
    /** Uma linha no rodapé, à esquerda dos botões. */
    rodape?: string;
    /** O que a linha da Home mostra à direita: a duração, quando o vídeo fechar ("1 min"). */
    duracao?: string;
    /** Último dia (AAAA-MM-DD) em que o modal abre sozinho e a Home mostra a linha. */
    ate: string;
}

/* O lançamento do Brik AI (08/10/2026): vídeo em qualquer tela.
   brik-ai-v7.mp4 de C:/Git/brik-reels/out: 1920×1080, 1:39, legenda gravada no vídeo.
   A capa é o quadro de 18,5 s da versão sem legenda. */
const BRIK_AI: Novidade = {
    chave: 'brik-ai-2026-10',
    titulo: 'Conheça o Brik AI',
    resumo: 'O assistente do seu Brik CRM: peça em uma frase e ele faz.',
    video: '/novidades/brik-ai.mp4',
    poster: '/novidades/brik-ai.jpg',
    duracao: 'Vídeo · 1:39',
    ate: '2026-11-08',
};

/* Tirar do funil quem não é lead (13/10/2026): só em Oportunidades, que é onde o
   fornecedor incomoda. A demo é a gravação da própria tela, na lista da Pipeline. */
const FORA_DO_FUNIL: Novidade = {
    chave: 'fora-do-funil-2026-10',
    titulo: 'Nem toda conversa é um negócio',
    resumo: 'Fornecedor, contador, grupo da família: agora dá para tirar do funil sem apagar a conversa.',
    rotas: ['/pipeline'],
    demo: '/novidades/fora-do-funil.mp4',
    demoEscura: '/novidades/fora-do-funil-escuro.mp4',
    poster: '/novidades/fora-do-funil.jpg',
    posterEscuro: '/novidades/fora-do-funil-escuro.jpg',
    passos: [
        {
            titulo: 'Entrou quem não é cliente',
            descricao: 'Quem fala com você pelo WhatsApp vira oportunidade — o fornecedor também.',
        },
        {
            titulo: 'Tire do funil',
            descricao: 'Pelo botão na linha da oportunidade, no menu da conversa no Chat ou na ficha do contato.',
        },
        {
            titulo: 'A conversa fica no Chat',
            descricao: 'Com as mensagens e o aviso de não lida. Sai da Pipeline e das métricas, e o robô não atende.',
        },
    ],
    rodape: 'Mudou de ideia? “Levar para o funil” traz de volta.',
    duracao: 'Novidade',
    ate: '2026-11-30',
};

export const NOVIDADES: Novidade[] = [BRIK_AI, FORA_DO_FUNIL];

/* ─── Tours: a novidade mostrada NA PRÓPRIA TELA ─────────────────────
   Balões que acendem o ponto da mudança e escurecem o resto (pedido dele, 17/10/2026).
   Mesmo "já vi" das novidades (`notification_reads`, tipo "novidade", pela `chave`), mas
   não passam pelo modal nem pela Home: quem os mostra é o `TourNovidade`, montado pela tela.

   O tour antigo de boas-vindas saiu em 26/09 porque envelheceu apontando para um botão que
   tinha mudado. Daí as três travas deste: o `alvo` é um `data-tour` da própria tela, balão
   sem alvo na tela é pulado (e sem nenhum alvo o tour não abre), e todo tour tem `ate`. */

export interface PassoTour {
    /** Seletor do elemento aceso — de preferência `[data-tour="..."]`, posto pela tela. */
    alvo: string;
    titulo: string;
    descricao: string;
}

export interface Tour {
    /** Vai para `notification_reads` (tipo "novidade"). Chave nova = tour novo para todo mundo. */
    chave: string;
    passos: PassoTour[];
    /** Último dia (AAAA-MM-DD) em que ele abre. */
    ate: string;
}

/* Painel Operacional (17/10/2026): números que abrem a lista e o bloco de Anúncios. */
export const TOUR_PAINEL: Tour = {
    chave: 'painel-numeros-2026-10',
    passos: [
        {
            alvo: '[data-tour="painel-kpi-entradas"]',
            titulo: 'Os números agora abrem a lista',
            descricao: 'Clique em qualquer número com tracejado e veja as oportunidades por trás dele. Cada uma abre numa nova aba.',
        },
        {
            alvo: '[data-tour="painel-entradas-dia"]',
            titulo: 'Vale também nos gráficos e na carga',
            descricao: 'Clique numa barra e veja o que entrou naquele dia. Os números de cada vendedor também abrem a lista.',
        },
        {
            alvo: '[data-tour="painel-anuncios"]',
            titulo: 'O resultado de cada anúncio',
            descricao: 'Quantos leads cada anúncio trouxe e quantos viraram venda, sem precisar conectar a conta de anúncios.',
        },
    ],
    ate: '2026-11-30',
};

/* Oportunidades (18/10/2026): o botão "Configurar funil", para a empresa saber que as
   etapas são dela. Só o Administrador tem o botão — sem ele na tela, o tour não abre. */
export const TOUR_CONFIGURAR_FUNIL: Tour = {
    chave: 'configurar-funil-2026-10',
    passos: [
        {
            alvo: '[data-tour="pipeline-configurar-funil"]',
            titulo: 'O funil agora é do seu jeito',
            descricao: 'Crie, renomeie e reordene as etapas do meio para seguir o seu processo de venda. Novo, Ganho e Perdido ficam fixos.',
        },
    ],
    ate: '2026-11-30',
};

export const tourNoPrazo = (t: Tour, agora = new Date()) => {
    const [a, m, d] = t.ate.split('-').map(Number);
    return agora <= new Date(a, m - 1, d, 23, 59, 59);
};

const noPrazo = (n: Novidade, agora: Date) => {
    const [a, m, d] = n.ate.split('-').map(Number);
    return agora <= new Date(a, m - 1, d, 23, 59, 59);
};

export const novidadesVigentes = (agora = new Date()): Novidade[] =>
    NOVIDADES.filter(n => noPrazo(n, agora));

/**
 * As novidades que podem abrir nesta tela, na ordem em que têm a vez: **a que pede esta
 * tela primeiro**, depois as que valem em todas. Uma novidade que fala da tela em que a
 * pessoa está chega na hora certa; o anúncio geral encontra a pessoa em qualquer outra.
 * Quem decide qual abre é o modal — ele sabe quais já foram vistas.
 */
export const novidadesDaRota = (rota: string, agora = new Date()): Novidade[] => {
    const servem = novidadesVigentes(agora).filter(n => !n.rotas || n.rotas.includes(rota));
    return [...servem.filter(n => n.rotas), ...servem.filter(n => !n.rotas)];
};

/** A novidade que vale em qualquer tela — a da apresentação do Brik, na Home. */
export const novidadeVigente = (agora = new Date()): Novidade | null =>
    novidadesVigentes(agora).find(n => !n.rotas) ?? null;

export const midiaDa = (n: Novidade): string | undefined => n.video ?? n.demo;

/* Uma conferência por arquivo, por carregamento da página: o modal e a Home perguntam a
   mesma coisa. O `content-type` importa: o servidor do SPA devolve o index.html com 200
   para arquivo que não existe. */
const conferencias = new Map<string, Promise<boolean>>();

export const midiaNoAr = (n: Novidade | null): Promise<boolean> => {
    const src = n && midiaDa(n);
    if (!src) return Promise.resolve(false);
    let p = conferencias.get(src);
    if (!p) {
        p = fetch(src, { method: 'HEAD' })
            .then(r => r.ok && (r.headers.get('content-type') ?? '').startsWith('video/'))
            .catch(() => false);
        conferencias.set(src, p);
    }
    return p;
};

/** Só sobre o vídeo do lançamento: é ele que substitui a apresentação do Brik na Home. */
export const videoDaNovidadeNoAr = (): Promise<boolean> => midiaNoAr(novidadeVigente());

/* ─── Combinados entre o modal (MainLayout) e a Home ─────────────────
   A Home pede para abrir uma novidade de novo (o bloco "Novidades"), e segura o modal
   enquanto o Brik se apresenta: as duas coisas apresentam o Brik, e uma por cima
   da outra seria demais. */
export const ABRIR_NOVIDADE_EVENT = 'lemeia:abrir-novidade';
export const HOME_LIVRE_EVENT = 'lemeia:home-livre';

let homeLivre = false;
export const homeEstaLivre = () => homeLivre;

/** A Home avisa quando a apresentação do Brik saiu da frente (ou nem vai aparecer). */
export const liberarHome = () => {
    if (homeLivre) return;
    homeLivre = true;
    window.dispatchEvent(new Event(HOME_LIVRE_EVENT));
};

/** Sem chave, a novidade que vale em qualquer tela (compatível com quem já chamava assim). */
export const abrirNovidade = (chave?: string) =>
    window.dispatchEvent(new CustomEvent(ABRIR_NOVIDADE_EVENT, { detail: { chave } }));
